import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Users, 
  Briefcase, 
  Calendar, 
  TrendingUp, 
  FileText, 
  CheckCircle,
  Clock,
  ArrowRight,
  BarChart3,
  Loader2
} from 'lucide-react';
import { employer } from '../../api';

const EmployerDashboard = () => {
  const navigate = useNavigate();
  const facilityName = localStorage.getItem('userName') || 'Hospital';
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeJobs: 0,
    totalApplicants: 0,
    matchedCandidates: 0,
    interviewsScheduled: 0,
    newThisWeek: 0
  });
  const [recentMatches, setRecentMatches] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userType = localStorage.getItem('userType');
    
    if (!token || userType !== 'employer') {
      navigate('/login');
      return;
    }
    
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [statsData, candidatesData] = await Promise.all([
        employer.getStats().catch(() => ({
          active_jobs: 0,
          total_applicants: 0,
          matched_candidates: 0,
          interviews_scheduled: 0,
          new_this_week: 0
        })),
        employer.getCandidates().catch(() => [])
      ]);

      setStats({
        activeJobs: statsData.active_jobs || 0,
        totalApplicants: statsData.total_applicants || 0,
        matchedCandidates: statsData.matched_candidates || 0,
        interviewsScheduled: statsData.interviews_scheduled || 0,
        newThisWeek: statsData.new_this_week || 0
      });

      // Format candidates for display
      const formattedCandidates = candidatesData.slice(0, 3).map(cand => ({
        id: cand._id || cand.user_id,
        name: cand.name || `${cand.first_name} ${cand.last_name}`,
        profession: cand.profession || 'Medical Professional',
        specialisation: cand.specialisation || '',
        country: cand.country || 'UK',
        experience: cand.experience || 0,
        aiScore: cand.ai_score || 75,
        skills: cand.skills || [],
        avatar: cand.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${cand.first_name || 'user'}`
      }));
      setRecentMatches(formattedCandidates);

      // Set recent activity (mock for now as backend doesn't have activity endpoint yet)
      setRecentActivity([
        { type: 'application', message: `${statsData.total_applicants || 'New'} applications received`, time: '2 hours ago' },
        { type: 'match', message: `${statsData.matched_candidates || 'New'} candidates matched to your positions`, time: '1 day ago' },
        { type: 'interview', message: `${statsData.interviews_scheduled || '0'} interviews scheduled`, time: 'This week' }
      ]);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#42b1c2] mx-auto mb-4" />
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Welcome, {facilityName}</h1>
            <p className="text-gray-600">Manage your recruitment pipeline and track candidate progress</p>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Active Jobs</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.activeJobs}</p>
                  </div>
                  <div className="w-12 h-12 bg-[#42b1c2]/10 rounded-lg flex items-center justify-center">
                    <Briefcase className="h-6 w-6 text-[#42b1c2]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Applicants</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.totalApplicants}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Matched</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.matchedCandidates}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Interviews</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.interviewsScheduled}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">New This Week</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.newThisWeek}</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-5 gap-4 mb-8">
            <Link to="/employer/post-job">
              <Card className="hover:shadow-lg transition-all hover:scale-105 cursor-pointer bg-gradient-to-br from-[#42b1c2] to-[#3a9fb0] text-white">
                <CardContent className="p-6 text-center">
                  <FileText className="h-8 w-8 mx-auto mb-2" />
                  <p className="font-semibold">Post New Job</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/employer/talent-pool">
              <Card className="hover:shadow-lg transition-all hover:scale-105 cursor-pointer bg-gradient-to-br from-purple-500 to-indigo-500 text-white">
                <CardContent className="p-6 text-center">
                  <Users className="h-8 w-8 mx-auto mb-2" />
                  <p className="font-semibold">Talent Pool</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/employer/candidates">
              <Card className="hover:shadow-lg transition-all hover:scale-105 cursor-pointer">
                <CardContent className="p-6 text-center">
                  <Users className="h-8 w-8 mx-auto mb-2 text-[#42b1c2]" />
                  <p className="font-semibold text-gray-900">Browse Candidates</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/employer/interviews">
              <Card className="hover:shadow-lg transition-all hover:scale-105 cursor-pointer">
                <CardContent className="p-6 text-center">
                  <Calendar className="h-8 w-8 mx-auto mb-2 text-[#42b1c2]" />
                  <p className="font-semibold text-gray-900">Manage Interviews</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/employer/analytics">
              <Card className="hover:shadow-lg transition-all hover:scale-105 cursor-pointer">
                <CardContent className="p-6 text-center">
                  <BarChart3 className="h-8 w-8 mx-auto mb-2 text-[#42b1c2]" />
                  <p className="font-semibold text-gray-900">View Analytics</p>
                </CardContent>
              </Card>
            </Link>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Top Matched Candidates */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Top Matched Candidates</CardTitle>
                  <Link to="/employer/candidates">
                    <Button variant="ghost" size="sm">
                      View All
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentMatches.length > 0 ? (
                      recentMatches.map((candidate) => (
                        <div key={candidate.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-[#42b1c2] transition-colors">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-start space-x-4">
                              <img src={candidate.avatar} alt={candidate.name} className="w-12 h-12 rounded-full" />
                              <div>
                                <h3 className="font-semibold text-gray-900">{candidate.name}</h3>
                                <p className="text-sm text-gray-600">{candidate.profession} • {candidate.specialisation}</p>
                                <p className="text-sm text-gray-500">{candidate.country} • {candidate.experience} years exp</p>
                              </div>
                            </div>
                            <Badge className="bg-[#42b1c2] hover:bg-[#3a9fb0]">AI Score: {candidate.aiScore}</Badge>
                          </div>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {candidate.skills.slice(0, 4).map((skill, index) => (
                              <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                {skill}
                              </span>
                            ))}
                          </div>
                          <div className="flex items-center space-x-2">
                            <Link to={`/employer/candidates/${candidate.id}`}>
                              <Button size="sm" className="bg-[#42b1c2] hover:bg-[#3a9fb0] text-white">
                                View Profile
                              </Button>
                            </Link>
                            <Button size="sm" variant="outline">
                              Request Interview
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No candidates yet</p>
                        <p className="text-sm text-gray-400 mt-1">Post jobs to start receiving matched candidates</p>
                        <Link to="/employer/post-job">
                          <Button className="mt-4 bg-[#42b1c2] hover:bg-[#3a9fb0] text-white">
                            Post Your First Job
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentActivity.map((activity, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-[#42b1c2] rounded-full mt-2"></div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{activity.message}</p>
                          <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button variant="ghost" size="sm" className="w-full mt-4">
                    View All Activity
                  </Button>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card className="bg-gradient-to-br from-[#42b1c2]/10 to-[#edd982]/10">
                <CardHeader>
                  <CardTitle className="text-lg">This Month</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">New Applications</span>
                      <span className="text-lg font-bold text-gray-900">42</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Interviews Conducted</span>
                      <span className="text-lg font-bold text-gray-900">15</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Offers Extended</span>
                      <span className="text-lg font-bold text-gray-900">8</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Hires Completed</span>
                      <span className="text-lg font-bold text-gray-900">5</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EmployerDashboard;